import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

/* -----------------------------------------------------------
   THE RIDE — Compact cinematic opener
   Just the ignition moment. Then hand off to the story.
------------------------------------------------------------ */

const Scooter = ({ headlightColor = "#ffcf78" }) => (
  <svg
    viewBox="0 0 220 130"
    className="w-[260px] md:w-[340px] h-auto drop-shadow-[0_35px_50px_rgba(0,0,0,0.7)]"
  >
    {/* Rider silhouette */}
    <g fill="#0b0d12">
      <ellipse cx="112" cy="26" rx="17" ry="18" />
      <path d="M97 20 Q112 5 128 20 L128 30 Q112 24 96 30 Z" />
      <path d="M99 22 Q112 15 126 22 L126 28 Q112 25 100 28 Z" fill={headlightColor} opacity="0.6" />
      <path d="M92 40 Q112 32 132 40 L142 82 Q112 76 84 82 Z" />
      <path d="M132 46 L172 62 L170 70 L128 58 Z" />
      <path d="M100 78 L94 108 L108 108 L114 82 Z" />
    </g>
    <g>
      <path d="M60 84 Q52 74 68 66 L104 66 L112 92 L74 96 Z" fill="#141821" />
      <path d="M62 82 Q56 76 70 70 L104 70 L110 88 L76 92 Z" fill={headlightColor} opacity="0.15" />
      <path d="M74 96 L154 96 L150 106 L74 106 Z" fill="#0e1218" />
      <path d="M148 60 Q168 58 178 76 L178 100 L152 100 L146 78 Z" fill="#141821" />
      <path d="M150 62 Q166 62 176 76 L176 82 L152 82 L150 74 Z" fill={headlightColor} opacity="0.28" />
      <ellipse cx="182" cy="82" rx="9" ry="6" fill={headlightColor} />
      <ellipse cx="182" cy="82" rx="4" ry="3" fill="#fff" opacity="0.9" />
      <rect x="170" y="52" width="10" height="14" rx="3" fill="#141821" />
      <rect x="168" y="48" width="14" height="6" rx="2" fill="#0b0d12" />
      <circle cx="72" cy="108" r="18" fill="#0b0d12" />
      <circle cx="72" cy="108" r="11" fill="#141821" />
      <circle cx="72" cy="108" r="4" fill={headlightColor} opacity="0.5" />
      <circle cx="164" cy="108" r="18" fill="#0b0d12" />
      <circle cx="164" cy="108" r="11" fill="#141821" />
      <circle cx="164" cy="108" r="4" fill={headlightColor} opacity="0.5" />
    </g>
  </svg>
);

const TheRide = () => {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const p = useSpring(scrollYProgress, { stiffness: 100, damping: 30, mass: 0.5 });

  // As user scrolls, the scooter builds up momentum: headlight brightens, scooter drifts forward, title fades to subtitle
  const headlightOpacity = useTransform(p, [0, 0.3, 0.6], [0, 1, 1]);
  const scooterX = useTransform(p, [0, 1], [0, 260]);
  const scooterBob = useTransform(p, (v) => Math.sin(v * 40) * 3);
  const titleY = useTransform(p, [0, 0.6], [0, -60]);
  const titleOpacity = useTransform(p, [0, 0.35, 0.7], [1, 1, 0]);
  const closingOpacity = useTransform(p, [0.55, 0.85, 1], [0, 1, 1]);
  const roadShift = useTransform(p, [0, 1], [0, -1400]);
  const sunY = useTransform(p, [0, 1], ["78%", "58%"]);
  const skyBoost = useTransform(p, [0, 1], [0.4, 0.9]);

  const scrollToStory = () => {
    document.getElementById("story")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="ride"
      ref={ref}
      className="relative"
      style={{ height: "180vh" }}
      data-testid="ride-section"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        {/* Sky — sunrise gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #1a0f1e 0%, #4a2436 30%, #d97e5e 65%, #f6c68f 88%, #ffe3b3 100%)",
          }}
        />

        {/* Sun */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            left: "68%",
            top: sunY,
            width: 200,
            height: 200,
            background:
              "radial-gradient(circle at 50% 50%, rgba(255,230,180,1) 0%, rgba(255,190,130,0.75) 35%, rgba(0,0,0,0) 72%)",
            filter: "blur(6px)",
            opacity: skyBoost,
          }}
        />

        {/* Distant hills */}
        <svg
          viewBox="0 0 1600 400"
          preserveAspectRatio="xMidYMid slice"
          className="absolute inset-x-0 bottom-0 w-full h-[65%] pointer-events-none"
        >
          <path
            d="M0 320 Q200 280 420 300 T880 290 T1400 280 L1600 300 L1600 400 L0 400 Z"
            fill="#000"
            opacity="0.55"
          />
          <path
            d="M0 350 Q300 320 620 335 T1200 340 T1600 335 L1600 400 L0 400 Z"
            fill="#000"
            opacity="0.75"
          />
          <path
            d="M0 380 L1600 380 L1600 400 L0 400 Z"
            fill="#000"
            opacity="0.95"
          />
        </svg>

        {/* Road lines */}
        <div className="absolute inset-x-0 bottom-0 h-[22%] pointer-events-none overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.9) 100%)",
            }}
          />
          <motion.div
            className="absolute left-0 bottom-[38%] right-0 h-[3px]"
            style={{
              background:
                "repeating-linear-gradient(90deg, rgba(255,255,255,0.85) 0 60px, transparent 60px 130px)",
              x: roadShift,
              opacity: 0.7,
            }}
          />
        </div>

        {/* Ambient particles */}
        {Array.from({ length: 22 }).map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white/80"
            style={{
              left: `${(i * 41) % 100}%`,
              top: `${25 + ((i * 37) % 55)}%`,
              opacity: 0.28,
              filter: "blur(0.5px)",
            }}
            animate={{
              x: [0, -40 - (i % 5) * 6, 0],
              y: [0, (i % 2 === 0 ? -1 : 1) * (5 + (i % 3) * 2), 0],
            }}
            transition={{
              duration: 7 + (i % 4),
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.2,
            }}
          />
        ))}

        {/* Scooter + rider (bottom-center, subtle drift) */}
        <motion.div
          className="absolute left-1/2 bottom-[9%] -translate-x-1/2 z-20"
          style={{ x: scooterX, y: scooterBob }}
        >
          <Scooter headlightColor="#ffcf78" />
          {/* Headlight beam — brightens on scroll */}
          <motion.div
            className="absolute right-[-30px] top-[54%] w-[260px] h-[130px] pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at left, rgba(255,207,120,0.75), transparent 65%)",
              filter: "blur(12px)",
              opacity: headlightOpacity,
              mixBlendMode: "screen",
            }}
          />
          {/* Ground shadow */}
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-[260px] h-4 rounded-full"
            style={{
              background: "radial-gradient(ellipse, rgba(0,0,0,0.6), transparent 70%)",
            }}
          />
        </motion.div>

        {/* Title (top area) — fades out as user scrolls */}
        <motion.div
          className="absolute inset-0 z-25 flex items-center justify-center pointer-events-none"
          style={{ y: titleY, opacity: titleOpacity }}
        >
          <div className="text-center px-6">
            <div className="text-[11px] tracking-[0.5em] uppercase text-[#e6c769] font-mono mb-6">
              A Cinematic Prologue
            </div>
            <h2
              className="text-[38px] md:text-[80px] leading-[1.02] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Every destination
              <br />
              <span className="italic text-[#e6c769]">tells a story.</span>
            </h2>
          </div>
        </motion.div>

        {/* "Start the Journey" prompt — bottom */}
        <motion.button
          onClick={scrollToStory}
          data-testid="ride-start-journey"
          className="absolute left-1/2 -translate-x-1/2 bottom-16 md:bottom-20 z-30 group"
          style={{ opacity: titleOpacity }}
          whileHover={{ scale: 1.04 }}
        >
          <div className="flex flex-col items-center gap-4">
            <span className="text-[11px] tracking-[0.45em] uppercase text-white/70 font-mono group-hover:text-white transition-colors">
              Start the Journey
            </span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="w-9 h-9 rounded-full border border-white/25 grid place-items-center group-hover:border-[#e6c769] transition-colors"
            >
              <ArrowDown className="w-4 h-4 text-white/70 group-hover:text-[#e6c769] transition-colors" />
            </motion.div>
          </div>
        </motion.button>

        {/* Closing frame — fades in near end */}
        <motion.div
          className="absolute inset-0 z-25 flex items-center justify-center pointer-events-none px-6"
          style={{ opacity: closingOpacity }}
        >
          <div className="text-center">
            <Sparkles className="w-6 h-6 text-[#e6c769] mx-auto mb-5" />
            <div className="text-[11px] tracking-[0.5em] uppercase text-[#e6c769]/80 font-mono mb-4">
              The Journey Begins
            </div>
            <h2
              className="text-[32px] md:text-[64px] leading-[1.05] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Keep scrolling.
              <br />
              <span className="italic text-[#e6c769]">The road unfolds below.</span>
            </h2>
          </div>
        </motion.div>

        {/* Fade into next section */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#06070b] pointer-events-none" />
      </div>
    </section>
  );
};

export default TheRide;
